import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800/90 text-white p-3 rounded-lg shadow-xl border border-slate-700 max-w-xs z-50 backdrop-blur-sm">
        <p className="font-bold text-amber-500 mb-2 border-b border-slate-600 pb-1">Carga: {label}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span style={{ color: '#0ea5e9' }}>Error Bruto:</span>
            <span className="font-mono font-bold ml-3" style={{ color: '#0ea5e9' }}>{Number(payload[0]?.payload?.valor1).toFixed(4)}%</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span style={{ color: '#10b981' }}>Error Neto:</span>
            <span className="font-mono font-bold ml-3" style={{ color: '#10b981' }}>{Number(payload[0]?.payload?.valor2).toFixed(4)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const CustomXAxisTick = ({ x, y, payload, data }) => {
  const item = data[payload.index];
  if (!item) return null;

  return (
    <g transform={`translate(${x},${y})`}>
      {/* Carga Number */}
      <text x={0} y={0} dy={12} textAnchor="middle" fill="#64748b" fontSize={11} fontWeight="bold">
        {item.carga}
      </text>
      {/* Date Label (Only if flagged) */}
      {item.dateLabel && (
        <text x={0} y={0} dy={28} textAnchor="middle" fill="#3b82f6" fontSize={10} fontWeight="bold">
          {item.dateLabel}
        </text>
      )}
    </g>
  );
};

const ChartCargaErrorPorVolumen = ({ promedioCargaErrorVolumen }) => {
  const [showInfo, setShowInfo] = useState(false);

  if (!promedioCargaErrorVolumen) return null;

  const data = [];
  Object.entries(promedioCargaErrorVolumen).forEach(([fecha, valores]) => {
    // Determine middle index to show date label
    const midIndex = Math.floor(valores.length / 2);

    valores.forEach((valor, i) => {
      // Only valid points
      if (valor.serie) {
        data.push({
          uniqueId: `${fecha}-${i}`,
          fecha: fecha,
          carga: i + 1, // 1-based index for Carga
          valor1: parseFloat(valor.valor1 || 0),
          valor2: parseFloat(valor.valor2 || 0),
          dateLabel: i === midIndex ? fecha : null // Mark middle for date label
        });
      }
    });
  });

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex flex-col h-[450px] relative">
      <div className="mb-6 flex justify-between items-start">
        <div className="flex items-start gap-2">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              ∿ Variación por Carga
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Detalle punto a punto del error bruto y neto.
            </p>
          </div>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="mt-1 text-slate-400 hover:text-blue-600 transition-colors"
            title="Ver fórmula de cálculo"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </button>

          {showInfo && (
            <div className="absolute top-14 left-4 z-50 bg-slate-800 text-white p-4 rounded-xl shadow-2xl border border-slate-700 w-72 animated fadeIn">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-amber-500 text-sm">Fórmula de Cálculo</h4>
                <button onClick={() => setShowInfo(false)} className="text-slate-400 hover:text-white"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Evaluación del Error de la medición en comparación con el patrón de referencia.
              </p>
              <div className="mt-3 text-xs bg-slate-900 p-2 rounded border border-slate-700 font-mono text-center mb-1">
                Err% = ((Vl - Vp) * 100) / Vp
              </div>
              <p className="text-xs text-slate-400 italic">Vl=Vol. Leído, Vp=Vol. Patrón</p>
            </div>
          )}
        </div>
        <div className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-bold rounded-full uppercase tracking-wider">
          Detalle de Dispersión
        </div>
      </div>

      <div className="flex-grow overflow-x-auto">
        <div className="min-w-[700px] h-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
            >
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis
                dataKey="uniqueId"
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
                interval={0}
                height={50} // More height for double labels
                tick={<CustomXAxisTick data={data} />} // Pass data to tick
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickFormatter={(value) => value.toFixed(3)}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />

              <ReferenceLine y={0.05} stroke="#cbd5e1" strokeDasharray="3 3" label={{ value: '+0.05', fontSize: 9, fill: '#94a3b8' }} />
              <ReferenceLine y={-0.05} stroke="#cbd5e1" strokeDasharray="3 3" label={{ value: '-0.05', fontSize: 9, fill: '#94a3b8' }} />
              <ReferenceLine y={0} stroke="#94a3b8" />

              <Line
                type="monotone"
                dataKey="valor1"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={{ r: 3, fill: "#0ea5e9" }}
                activeDot={{ r: 6 }}
                name="Error Bruto"
              />

              <Line
                type="monotone"
                dataKey="valor2"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3, fill: "#10b981" }}
                activeDot={{ r: 6 }}
                name="Error Neto"
              />
              <Brush
                dataKey="uniqueId"
                height={30}
                stroke="#64748b"
                travellerWidth={10}
                fill="#f8fafc"
                tickFormatter={() => ""} // Clean brush ticks
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ChartCargaErrorPorVolumen;
